import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Zap, ArrowRight, Clock, Calendar, User } from "lucide-react";

// Analytics helper
export const trackBlogCTA = (post, cta, location) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'blog_cta_click', {
      post: post,
      cta: cta,
      location: location
    });
  }
};

// CTA Block Component
export const CTABlock = ({ postSlug, location, variant = "default" }) => {
  const isCompact = variant === "compact";
  
  return (
    <div className={`${isCompact ? 'my-8' : 'my-12'} p-6 md:p-8 bg-primary/5 border border-primary/20 rounded-xl`}>
      <div className={`${isCompact ? '' : 'text-center'}`}>
        <h3 className={`${isCompact ? 'text-lg' : 'text-xl md:text-2xl'} font-bold text-foreground mb-2`}>
          {location === 'top' ? 'Ready to see AI scoring in action?' : 
           location === 'mid' ? 'Want to try this for your business?' : 
           'Start qualifying leads smarter today'}
        </h3>
        <p className="text-muted-foreground mb-4">
          {location === 'footer' 
            ? 'See your best leads immediately — and stop wasting time on low-quality submissions.'
            : 'HeyIntake scores and prioritizes every submission automatically.'}
        </p>
        <div className={`flex ${isCompact ? '' : 'justify-center'} gap-3 flex-wrap`}>
          <Link to="/register" onClick={() => trackBlogCTA(postSlug, 'trial', location)}>
            <Button>
              Start 7-day free trial
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          <Link to="/demo" onClick={() => trackBlogCTA(postSlug, 'demo', location)}>
            <Button variant="outline">Book a demo</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Key Takeaways Component
export const KeyTakeaways = ({ items }) => (
  <div className="my-8 p-6 bg-muted/30 border-l-4 border-primary rounded-r-lg">
    <h2 className="text-lg font-bold text-foreground mb-3">Key Takeaways</h2>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-foreground">
          <span className="text-primary font-bold">•</span>
          {item}
        </li>
      ))}
    </ul>
  </div>
);

// FAQ Section with Schema
export const FAQSection = ({ faqs, postSlug }) => (
  <section className="my-12">
    <h2 className="text-2xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
    <Accordion type="single" collapsible className="bg-white rounded-lg shadow-sm border">
      {faqs.map((faq, i) => (
        <AccordionItem key={i} value={`faq-${i}`} className="border-b last:border-b-0">
          <AccordionTrigger className="px-6 py-4 text-left font-semibold hover:no-underline">
            {faq.question}
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-4 text-muted-foreground">
            {faq.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

// Blog Post Meta
export const PostMeta = ({ author, date, readTime }) => (
  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
    <div className="flex items-center gap-1">
      <User className="w-4 h-4" />
      <span>{author}</span>
    </div>
    <div className="flex items-center gap-1">
      <Calendar className="w-4 h-4" />
      <span>{date}</span>
    </div>
    <div className="flex items-center gap-1">
      <Clock className="w-4 h-4" />
      <span>{readTime} min read</span>
    </div>
  </div>
);

// Blog Navigation
export const BlogNav = () => (
  <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center h-16">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-foreground">HeyIntake</span>
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground">Blog</Link>
          <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground hidden sm:block">Pricing</Link>
          <Link to="/register">
            <Button size="sm">Start Trial</Button>
          </Link>
        </div>
      </div>
    </div>
  </nav>
);

// Blog Footer
export const BlogFooter = () => (
  <footer className="py-12 px-4 bg-white border-t mt-16">
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mb-6">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <Link to="/blog" className="hover:text-foreground">Blog</Link>
        <Link to="/pricing" className="hover:text-foreground">Pricing</Link>
        <Link to="/demo" className="hover:text-foreground">Demo</Link>
        <Link to="/compare/heyintake-vs-intakeq-vs-jotform" className="hover:text-foreground">Comparisons</Link>
        <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
      </div>
      <p className="text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} HeyIntake. All rights reserved.
      </p>
    </div>
  </footer>
);

// Competitor Disclaimer
export const CompetitorDisclaimer = () => (
  <p className="text-xs text-muted-foreground italic my-4">
    Competitor positioning is based on public product information and may change.
  </p>
);

// Generate Article Schema
export const generateArticleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.description,
  "author": {
    "@type": "Person",
    "name": article.author
  },
  "publisher": {
    "@type": "Organization",
    "name": "HeyIntake",
    "logo": {
      "@type": "ImageObject",
      "url": "https://heyintake.com/logo.png"
    }
  },
  "datePublished": article.datePublished,
  "dateModified": article.dateModified || article.datePublished,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `https://heyintake.com${article.url}`
  }
});

// Generate FAQ Schema
export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});
